import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { prettifyApiError } from "@/shared/api";
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import { RailLogo } from "@/shared/ui/RailLogo";
import { isMfaRichiesta, login } from "./api";

/**
 * Pagina di login (web-mode). Route piena FUORI dall'AppShell: niente rail,
 * form centrato. Al successo torna alla route di provenienza (query ``next``
 * scritta dalla guardia 401 di shared/api) oppure a /ordini.
 *
 * 🔑 Dal 2026-07-22 questa e' la PRIMA cosa che si vede: prima davanti c'era
 * il `basic_auth` di Caddy, cioe' il popup grigio del browser, che conosceva
 * un solo utente (`davide`) — per questo Annalisa non riusciva ad accedere pur
 * avendo l'account. Tolto quello, questa pagina e' la porta d'ingresso vera.
 *
 * Register «product» (la UI SERVE il compito): niente decorazione, ma tutti
 * gli stati che servono davvero a chi non entra — credenziali sbagliate,
 * troppi tentativi, BLOC MAIUSC acceso, e a chi chiedere se resta fuori.
 */
export function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [codice, setCodice] = useState("");
  // Il campo del codice compare solo dopo che il backend l'ha chiesto: chi non
  // ha il secondo fattore non deve nemmeno vederlo.
  const [mfaRichiesta, setMfaRichiesta] = useState(false);
  const [mostraPassword, setMostraPassword] = useState(false);
  const [maiuscole, setMaiuscole] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Solo path interni: un ``next`` assoluto o protocol-relative ("//evil.com")
  // sarebbe un open redirect verso un sito arbitrario.
  const rawNext = params.get("next");
  const next = rawNext?.startsWith("/") && !rawNext.startsWith("//") ? rawNext : "/ordini";
  // La guardia 401 rimanda qui con ``next``: dirlo evita il «perche' sono
  // tornato al login?» invece di lasciarlo intuire.
  const sessioneScaduta = Boolean(rawNext);

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const esito = await login(username, password, mfaRichiesta ? codice : undefined);
      // Password scaduta: si entra lo stesso, ma si va dove si cambia — e la
      // pagina lo dice. Bloccare l'accesso chiuderebbe fuori l'unico
      // amministratore il giorno della scadenza.
      const destinazione = esito.passwordScaduta ? "/impostazioni?password_scaduta=1" : next;
      void navigate(destinazione, { replace: true });
    } catch (err) {
      if (isMfaRichiesta(err)) {
        // Non e' un errore: la password era giusta, manca il secondo passo.
        setMfaRichiesta(true);
        setCodice("");
        setBusy(false);
        return;
      }
      // 400 (credenziali) e 429 (rate limit) arrivano con detail gia' in
      // italiano dal backend: prettifyApiError li passa puliti.
      setError(prettifyApiError(err));
      // La password si svuota, il nome utente NO: chi sbaglia a digitare
      // riprova solo il campo sbagliato invece di riscrivere tutto.
      setPassword("");
      // Il codice e' usa-e-getta: quello appena rifiutato non vale piu'.
      setCodice("");
    } finally {
      setBusy(false);
    }
  }

  /** BLOC MAIUSC acceso: la causa piu' banale di «la password non funziona»,
   *  e l'unica invisibile perche' il campo e' mascherato. */
  function controllaMaiuscole(event: React.KeyboardEvent<HTMLInputElement>) {
    setMaiuscole(event.getModifierState?.("CapsLock") ?? false);
  }

  const campo =
    "h-11 w-full rounded-md border border-line bg-paper px-3 text-[14px] text-ink " +
    "transition-colors duration-150 placeholder:text-ink-4 " +
    "focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 " +
    "focus-visible:ring-accent-soft";

  return (
    <main className="flex min-h-screen flex-col items-center justify-center bg-paper-2 px-4 py-10">
      <div className="w-full max-w-[400px]">
        <div className="mb-7 flex flex-col items-center gap-3 text-center">
          <RailLogo size={56} />
          <div>
            <h1 className="font-serif text-[27px] font-medium tracking-tight text-ink">
              Order Manager
            </h1>
            <p className="mt-1.5 text-[13px] leading-relaxed text-ink-3">
              {sessioneScaduta
                ? "La sessione è scaduta: accedi di nuovo per riprendere da dove eri."
                : "Accedi con il tuo account per continuare."}
            </p>
          </div>
        </div>

        <form
          onSubmit={(event) => void submit(event)}
          className="rounded-lg border border-line bg-card px-6 py-6 shadow-cardOpen"
          noValidate
        >
          {/* L'errore sta SOPRA i campi, non in fondo: chi sbaglia deve
              leggerlo prima di riprovare, non dopo aver ripremuto il tasto.
              Il componente condiviso decide tinta e corpo (ha gia' role=alert):
              qui si passa solo il margine, altrimenti tornano le 20 varianti. */}
          <ErrorBanner message={error} className="mb-4 block" />

          <label htmlFor="login-utente" className="block text-[12.5px] font-medium text-ink-2">
            Nome utente
          </label>
          <input
            id="login-utente"
            type="text"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            autoComplete="username"
            autoFocus
            required
            disabled={busy}
            className={`mt-1.5 ${campo} disabled:opacity-60`}
          />

          <div className="mt-4 flex items-baseline justify-between">
            <label htmlFor="login-password" className="text-[12.5px] font-medium text-ink-2">
              Password
            </label>
            <button
              type="button"
              onClick={() => setMostraPassword((v) => !v)}
              className="rounded text-[11.5px] font-medium text-ink-3 transition-colors duration-150 hover:text-accent-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-soft"
            >
              {mostraPassword ? "Nascondi" : "Mostra"}
            </button>
          </div>
          <input
            id="login-password"
            type={mostraPassword ? "text" : "password"}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            onKeyUp={controllaMaiuscole}
            onBlur={() => setMaiuscole(false)}
            autoComplete="current-password"
            required
            disabled={busy}
            className={`mt-1.5 ${campo} disabled:opacity-60`}
          />

          {/* Avviso, non errore: la tinta ambra dice «guarda qui», non
              «hai sbagliato». Annunciato agli screen reader perche' e'
              esattamente chi non vede la tastiera ad averne bisogno. */}
          {maiuscole && (
            <p role="status" className="mt-2 text-[11.5px] leading-snug text-orange-800">
              Il BLOC MAIUSC è acceso: la password distingue maiuscole e minuscole.
            </p>
          )}

          {/* Secondo passo: compare solo quando il backend l'ha chiesto, con il
              campo gia' a fuoco — chi ha l'app aperta digita e basta.
              inputMode numerico: sul telefono esce il tastierino, non la
              tastiera intera. */}
          {mfaRichiesta && (
            <div className="mt-4 rounded-md border border-line bg-paper px-3.5 py-3">
              <label htmlFor="login-codice" className="block text-[12.5px] font-medium text-ink-2">
                Codice di verifica
              </label>
              <p className="mt-1 text-[11.5px] leading-snug text-ink-3">
                Le sei cifre che l'app di autenticazione mostra adesso.
              </p>
              <input
                id="login-codice"
                type="text"
                value={codice}
                onChange={(event) => setCodice(event.target.value)}
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                required
                maxLength={7}
                disabled={busy}
                className={`mt-2 ${campo} font-mono tracking-[0.3em] disabled:opacity-60`}
              />
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="mt-5 h-11 w-full rounded-md border border-accent bg-accent-2 text-[13.5px] font-medium text-white transition-[filter,opacity] duration-150 hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-soft disabled:opacity-60"
          >
            {busy ? "Accesso in corso…" : mfaRichiesta ? "Verifica e accedi" : "Accedi"}
          </button>
        </form>

        {/* Un tool interno non ha il «password dimenticata»: c'e' Davide.
            Dirlo qui evita il messaggio «non riesco a entrare» senza contesto. */}
        <p className="mt-5 text-center text-[11.5px] leading-relaxed text-ink-3">
          Non riesci ad accedere? L'account lo crea e lo reimposta Davide, da{" "}
          <span className="whitespace-nowrap">Impostazioni → Utenti</span>.
        </p>
      </div>
    </main>
  );
}
